import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function FamilyTreeProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study familytree-project container mt-120">
      <div className="project-hero-card bg-secondary radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 sm:f-32 xs:f-18 xs:l-h-24 l-h-40 f-w-600 text-primary words1">
            FamilyTree
          </h1>
          <p className="f-16 xs:f-14 xs:l-h-20 l-h-30 f-w-300 text-primary mt-8 sentence1">
            <span>B2C Mobile + Web · Family App · Calibraint Technologies</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        A family relationship and memory app where users can build their
        family tree, store photos, videos, and audio, and share moments
        privately with family members. I worked as both designer and
        React.js developer on this one. We went through 6 product milestones
        before shipping the beta — roughly a year of design and build.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">B2C App</span>
        <span className="cs-pill">Designer + React Dev</span>
        <span className="cs-pill">6 Milestones</span>
        <span className="cs-pill">Calibraint Technologies</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>6</div>
          <div className="cs-stat-l">Milestones shipped</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>2</div>
          <div className="cs-stat-l">Tree structures tested</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fb923c" }}>1yr</div>
          <div className="cs-stat-l">Design to beta</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#a78bfa" }}>✓</div>
          <div className="cs-stat-l">Beta live with users</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The product</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          FamilyTree lets families store memories — photos, audio, video —
          and connect them to real relationships in a family tree. Families
          can invite relatives to join and grow the tree together. Sounds
          simple, but combining a relationship graph with a cloud storage
          system created some genuinely tricky UX problems that took us
          several iterations to solve.
        </p>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          UI/UX designer and frontend developer (React.js). I handled the
          full design — research, wireframes, design system, high-fidelity
          screens — and then also implemented the screens in React.js
          alongside the development team across all 6 milestone sprints.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          What was already out there and where the gap was
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Looked at Google Photos, Apple Memories, Ancestry, and
          FamilySearch. Google Photos was great at storage but had zero
          relationship awareness. Ancestry was rich but felt academic and
          cold — not something a regular family member would open for fun.
          The gap was clear: something warm and personal, not a records
          archive.
        </p>
        <div className="cs-quote">
          <p>
            "I want my kids to know where they came from — not just names in
            a chart, but the actual stories and photos and feeling of being
            part of a family."
          </p>
          <span>— User interview</span>
        </div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-white radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 familytree-research-1"></div>
        <div className="cs-img bg-white radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 familytree-research-2"></div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Pain points</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Adding a new family member to a tree felt like editing a
              database — relationship logic was exposed to the user instead
              of handled in the background
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Photos of a person lived in a completely separate place from
              that person's node in the family tree — no connection between
              media and relationships
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Sharing memories lacked context — a shared photo without any
              story behind it is just a file transfer, not a memory
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              The shared family drive had no structure by person or
              relationship — everything flat, hard to find anything specific
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Inviting a relative to join broke the tree layout — visual
              structure would shift and confuse existing members
            </div>
          </div>
        </div>
        <div className="cs-quote">
          <p>
            The tree needed to feel like something you're growing together —
            not a form you're filling in.
          </p>
        </div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          The calls that shaped the product
        </h3>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Chip-based tree growth</h4>
            <p>
              Tried two approaches — thread style and chip-based step-by-step.
              Users clearly preferred adding one family member at a time.
              Less visually impressive but way less overwhelming for
              non-technical family members.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Media tied to people</h4>
            <p>
              Every photo, video, and audio is associated with one or more
              family members. Open someone's profile and their media is
              right there — no separate drive navigation needed.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Family Surprise flow</h4>
            <p>
              A dedicated flow for sharing special moments — birthdays,
              announcements — with a reveal mechanic. Kept separate from
              normal uploads so it doesn't get buried in the feed.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Drive states designed separately</h4>
            <p>
              Invite, upload, shared, and drive-view each got their own
              screen with distinct visual treatment. Users always knew
              exactly what state they were in and what action was
              available.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 familytree-img1"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 familytree-img2"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 ml-15 xs:ml-0 xs:mr-0 xs:mt-20 box1 familytree-img3"></div>
        <div className="cs-img bg-white radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 familytree-research-3"></div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 familytree-img4"></div>
      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 familytree-img5"></div>

      <div className="mt-32 mb-32">
        <div className="cs-lbl">Handoff &amp; build</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Once the tree and profile flows were validated, built out the full
          design system and handed off to the mobile and web teams. Every
          state was accounted for — nothing left to interpretation during
          build. Because I was also the developer, I could catch and fix
          design issues directly in code during the milestone sprints, which
          made things significantly smoother.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">🚀</div>
            <h4>Beta shipped</h4>
            <p>
              All 6 milestones completed. Beta released for real users
              about a year from first wireframe.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🌳</div>
            <h4>Tree structure validated</h4>
            <p>
              Tested two approaches. Chip-based model won clearly — simpler
              for all ages including non-technical family members.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">☁️</div>
            <h4>Full drive system</h4>
            <p>
              All drive states designed — upload, invite, shared, individual
              view — across both web and mobile.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">📱</div>
            <h4>Web + mobile parity</h4>
            <p>
              Consistent design system across both platforms. Components
              built once, adapted for both.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🎁</div>
            <h4>Surprise feature shipped</h4>
            <p>
              Made it into the beta — one of the more unique features that
              no competitor had at the time.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">⚛️</div>
            <h4>Built it too</h4>
            <p>
              Implemented in React.js alongside dev team. Fewer handoff
              errors, quicker fixes during build.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Cloud storage UX is more complex than it looks.</strong>{" "}
            Every permission state needs its own screen and message. You
            can't just design the happy path and hope devs figure out the
            rest.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Relationship data is tricky to make approachable.</strong>{" "}
            A family tree is technically a graph. Designing it so a
            grandparent can use it without help meant hiding all the
            complexity — users should never feel like they're editing a
            data structure.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Being the developer changed how I design.</strong>{" "}
            Writing React.js for my own screens made me stop designing
            things that looked good but were expensive to build for
            minimal UX gain. I started thinking about component reuse from
            the first wireframe.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> More testing with older
            family members — the real target audience. Our reviews were
            mostly younger stakeholders, and some flows needed more
            hand-holding than we expected.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="familytree" />
        </div>
        <ProjectNav current="familytree" />
      </div>
    </div>
  );
}
